import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { SelectContent } from '../select-content';
import { SelectTrigger } from '../select-trigger';

import { SelectProps } from './types';
import { Popover } from '@redsift/popovers';
import { SelectContext } from './context';
import {
  ListboxReducer,
  ListboxState,
  ListboxContextProps,
  FocusWithinGroup,
  ListboxContext,
  Flexbox,
  Text,
  ListboxActionType,
  Theme,
  useTheme,
  ThemeProvider,
} from '@redsift/design-system';

const COMPONENT_NAME = 'Select';
const CLASSNAME = 'redsift-select';

/**
 * Select allows users to choose a single option from a dropdown list.
 * Built on Floating UI with keyboard navigation and screen reader support.
 *
 * Composed of `Select.Trigger` and `Select.Content` sub-components.
 * Use the `Item` component for options.
 *
 * For searchable/filterable lists, use Combobox instead.
 *
 * @example
 * // Basic select with TextField trigger (default)
 * <Select label="Country" onChange={(value) => setCountry(value)}>
 *   <Select.Trigger placeholder="Select a country" />
 *   <Select.Content>
 *     <Item value="us">United States</Item>
 *     <Item value="uk">United Kingdom</Item>
 *     <Item value="ca">Canada</Item>
 *   </Select.Content>
 * </Select>
 *
 * @example
 * // Button trigger variant
 * <Select label="Actions">
 *   <Select.Trigger variant="button">Choose action</Select.Trigger>
 *   <Select.Content>
 *     <Item value="edit">Edit</Item>
 *     <Item value="delete">Delete</Item>
 *   </Select.Content>
 * </Select>
 *
 * @example
 * // Controlled select with default value
 * <Select
 *   label="Status"
 *   value={status}
 *   defaultValue="active"
 *   onChange={setStatus}
 * >
 *   <Select.Trigger />
 *   <Select.Content>
 *     <Item value="active">Active</Item>
 *     <Item value="inactive">Inactive</Item>
 *     <Item value="pending">Pending</Item>
 *   </Select.Content>
 * </Select>
 *
 * @example
 * // Render function for custom trigger content
 * <Select label="Sort by">
 *   <Select.Trigger>
 *     {({ selected }) => (
 *       <Button rightIcon={mdiChevronDown}>
 *         {selected?.label || 'Select'}
 *       </Button>
 *     )}
 *   </Select.Trigger>
 *   <Select.Content>...</Select.Content>
 * </Select>
 *
 * @example
 * // With validation
 * <Select label="Priority" isRequired isInvalid={!priority}>
 *   <Select.Trigger placeholder="Choose priority" />
 *   <Select.Content>
 *     <Item value="low">Low</Item>
 *     <Item value="medium">Medium</Item>
 *     <Item value="high">High</Item>
 *   </Select.Content>
 * </Select>
 */
export const BaseSelect: React.FC<SelectProps> & {
  displayName?: string;
  className?: string;
} = (props) => {
  const {
    color,
    defaultValue,
    description,
    descriptionProps,
    isDisabled,
    isInvalid,
    minWidth = 'trigger-width',
    onChange,
    theme: propsTheme,
    triggerClassName,
    value,
    variant,
    wrapperProps,
  } = props;

  const theme = useTheme(propsTheme);

  const [selectedValue, setValue] = useState<string>(value || defaultValue || '');
  useEffect(() => {
    if (value) {
      state.setValue(value);
    }
  }, [value]);

  /** Listbox context */
  const [listboxState, listboxDispatch] = useReducer(ListboxReducer, {
    isDisabled,
    selectedValues: [selectedValue],
  } as ListboxState);
  const listboxContext = useMemo<ListboxContextProps>(
    () => ({
      state: listboxState,
      dispatch: listboxDispatch,
    }),
    [listboxState]
  );
  useEffect(() => {
    listboxDispatch({
      type: ListboxActionType.UPDATE_OPTIONS,
      payload: {
        isDisabled: isDisabled || false,
      },
    });
  }, [isDisabled, theme]);

  /** Select context. */
  const state = {
    color,
    isDisabled: isDisabled || false,
    isInvalid: isInvalid || false,
    setValue(value: string) {
      if (isDisabled) {
        return;
      }

      const previousValue = selectedValue;
      setValue(value);
      if (value !== previousValue && onChange) {
        onChange(value);
      }
    },
    theme,
    triggerClassName,
    value: selectedValue,
    variant,
  };

  return (
    <ThemeProvider value={{ theme }}>
      <FocusWithinGroup focusType="virtual-focus" focusOnInit={false}>
        <SelectContext.Provider value={state}>
          <ListboxContext.Provider value={listboxContext}>
            <Flexbox flexDirection="column" alignItems="flex-start" gap="0px" {...wrapperProps}>
              <Popover
                theme={theme}
                overrideDisplayName={{ content: 'SelectContent', trigger: 'SelectTrigger' }}
                placement="bottom-start"
                role="listbox"
                minWidth={minWidth}
                {...props}
              />
              {description && typeof description === 'string' ? (
                <Text
                  theme={theme}
                  marginTop="8px"
                  variant="caption"
                  color={isInvalid ? 'error' : theme === Theme.light ? 'dark-grey' : 'white'}
                  {...descriptionProps}
                >
                  {description}
                </Text>
              ) : description ? (
                description
              ) : null}
            </Flexbox>
          </ListboxContext.Provider>
        </SelectContext.Provider>
      </FocusWithinGroup>
    </ThemeProvider>
  );
};
BaseSelect.className = CLASSNAME;
BaseSelect.displayName = COMPONENT_NAME;

export const Select = Object.assign(BaseSelect, {
  Trigger: SelectTrigger,
  Content: SelectContent,
});
